import {
    Button,
    Chip,
    CircularProgress,
    Divider, Grid, IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Box,
    Typography,
} from "@mui/material";
import {Add, Delete, PlayArrow, Stop, Info} from "@mui/icons-material";
import {
    LiveStreamRequest,
    StreamTarget,
    StreamTargetList,
    StreamTargetStatus,
    StreamTargetType
} from "../StreamTarget/streamTargetDTO";
import {StreamTargetIntegration} from "../StreamTargetIntegration/streamTargetIntegrationDTO";
import {fetchStreamTargetIntegrations} from "../StreamTargetIntegration/streamTargetIntegrationActions";
import {connect} from "react-redux";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {streamTargetService} from "../../services/stream-target";
import {
    addNewStreamTargetToChannelRequest,
    addStreamTargetToChannelRequest,
    removeStreamTargetFromChannelRequest, updateChannelStreamTargetUuidRequest
} from "./channelActions";
import {Channel, ChannelMap, ChannelStatus, ChannelStreamTargets} from "./ChannelDTO";
import {LiveEvent} from "../Events/EventsDTO";
import classNames from "classnames";
import {createAndStartLiveStreamRequest, stopLiveStreamRequest} from "../StreamTarget/streamTargetActions";
import {Profile} from "../User/userDTO";
import styles from "./ChannelControl-jss";
import ChannelControlStartButton from "./ChannelControlStartButton";
import useFeatureFlags from "../FeatureFlag/useFeatureFlags";
import {withStyles} from "@mui/styles";
import {v4 as uuidv4} from "uuid";
import {
    Live4tvLiveStreamRequest,
    isInstanceOfLive4tvApiStreamTarget,
    Live4tvListStreamTargetsRequest,
    isScheduledLive4tvApiStreamTarget,
} from "../StreamTargetLive4tvApi/live4tvApiDTO";
import {
    createAndStartLive4tvApiLiveStream, listTargets,
    stopLive4tvApiLiveStream
} from "../StreamTargetLive4tvApi/live4tvApiActions";
import {useNavigate} from "react-router-dom";

type Properties = {
    classes: any,
    liveEvent: LiveEvent,
    streamTargets: StreamTargetList,
    channels: ChannelMap,
    profile: Profile,
    streamTargetIntegrations: Array<StreamTargetIntegration>,
    fetchStreamTargetIntegrations: Function,
    addNewStreamTargetToChannelRequest: Function,
    updateChannelStreamTargetUuidRequest: Function,
    addStreamTargetToChannelRequest: Function,
    removeStreamTargetFromChannelRequest: Function,
    createAndStartLiveStreamRequest: Function,
    stopLiveStreamRequest: Function,
    createAndStartLive4tvApiLiveStream: Function,
    stopLive4tvApiLiveStream: Function,
    listTargets: Function,
    setError: Function,
}

function ChannelControl(props: Properties) {
    const {
        classes,
        liveEvent,
        streamTargets,
        channels,
        profile,
        streamTargetIntegrations,
        fetchStreamTargetIntegrations,
        addNewStreamTargetToChannelRequest,
        updateChannelStreamTargetUuidRequest,
        addStreamTargetToChannelRequest,
        removeStreamTargetFromChannelRequest,
        createAndStartLiveStreamRequest,
        stopLiveStreamRequest,
        createAndStartLive4tvApiLiveStream,
        stopLive4tvApiLiveStream,
        listTargets,
        setError,
    } = props;

    const [addStreamTargetMenuEl, setAddStreamTargetMenuEl] = useState(null);
    const isAddStreamTargetMenuOpen = Boolean(addStreamTargetMenuEl);

    const [channel, setChannel] = useState<Channel>();

    const {t} = useTranslation();

    const navigate = useNavigate();

    const [flagGoLive] = useFeatureFlags(profile.token, 'app_can_go_live');

    useEffect(() => {
        if (!profile?.token || !profile?.code) {
            return;
        }

        listTargets({
            profile,
        });
        fetchStreamTargetIntegrations(profile.token);
    }, [fetchStreamTargetIntegrations, listTargets, profile]);

    useEffect(() => {
        console.debug('channels', channels);
        setChannel(channels[liveEvent.code]);
        cleanDeletedStreamTargets();
    }, [channels]);

    useEffect(() => {
        // Check for error messages in stream targets and display them when status changes to START_FAILED
        if (channel) {
            Object.keys(channel.streamTargets).forEach((channelStreamTargetKey: string) => {
                let streamTarget: StreamTarget = streamTargets[channelStreamTargetKey];
                if (streamTarget && streamTarget.status === StreamTargetStatus.START_FAILED) {
                    if (streamTarget.errorReason) {
                        // Use the same translation pattern as frontend validation
                        setError(t('app_can_go_live.' + streamTarget.errorReason, Object.assign({}, {interpolation: {escapeValue: false}}, streamTarget.errorAdditionalData)));
                    } else if (streamTarget.errorMessage) {
                        // Fallback to plain error message if no reason code
                        setError(streamTarget.errorMessage);
                    }
                }
            });
        }
    }, [streamTargets, channel, t]);

    const cleanDeletedStreamTargets = () => {
        if (channels[liveEvent.code]) {
            Object.keys(channels[liveEvent.code].streamTargets).forEach((key: string) => {
                if (streamTargets[key] === undefined) {
                    removeStreamTargetFromChannelRequest(channels[liveEvent.code], channels[liveEvent.code].streamTargets[key]);
                }
            });
        }
    }

    const isTargetScheduled = (streamTarget: StreamTarget) => {
        return isScheduledLive4tvApiStreamTarget(streamTarget);
    }

    const handleAddStreamTarget = (streamTarget: StreamTarget) => {
        console.debug('handleAddStreamTarget', streamTarget);
        setAddStreamTargetMenuEl(null);
        addStreamTargetToChannelRequest(channel, streamTarget);
    }

    const handleAddStreamTargetAccount = (streamTargetIntegration: StreamTargetIntegration) => {
        console.debug('handleAddStreamTargetAccount', streamTargetIntegration);
        setAddStreamTargetMenuEl(null);
        addNewStreamTargetToChannelRequest(channel, streamTargetIntegration);
    }

    const listStreamTargetMenuItems = () => {
        let targets: Array<any> = [];
        Object.keys(streamTargets).forEach((key: string) => {
            const target = streamTargets[key];
            if (channel !== undefined
                && channel.streamTargets !== undefined
                && channel.streamTargets[target.key] === undefined
            ) {
                const Icon = streamTargetService.getIconForTarget(target.type);
                targets.push(<MenuItem
                    key={target.key}
                    onClick={() => handleAddStreamTarget(target)}
                    sx={{ py: 1.25, alignItems: "flex-start" }}
                >
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
                    <Box
                        sx={{
                            flexGrow: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <ListItemText
                            primary={target.title}
                            secondary={isTargetScheduled(target) ? t("Scheduled live") : null}
                            primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        {isTargetScheduled(target) ? (
                            <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" color="text.secondary">
                                {renderScheduledNetworkIcons(target, "menu")}
                            </Box>
                        ) : null}
                    </Box>
                </MenuItem>)
            }
        });

        return targets;
    }

    const listStreamTargetIntegrationsMenuItems = () => {
        let  integrations: Array<any> = [];
        streamTargetIntegrations.forEach((streamTargetIntegration: StreamTargetIntegration) => {
            if (streamTargetIntegration.code === 'instagram' && profile.email === 'facebook.user@facebook.com') {
            } else {
                integrations.push(<MenuItem key={streamTargetIntegration.code} onClick={() => handleAddStreamTargetAccount(streamTargetIntegration)}>
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    <ListItemText>{t('Add new {{integration}}', {integration: t(streamTargetIntegration.code)})}</ListItemText>
                    { streamTargetIntegration.code === StreamTargetType.TIKTOK ?
                        <>
                            &nbsp;&nbsp;
                            <Chip size="small" label={ t('PRO') } color="primary" />
                        </>
                        : null }
                </MenuItem>)
            }
        });

        return integrations;
    }

    function getStatusChip(status: StreamTargetStatus, streamTarget?: StreamTarget) {
        const label = t(status) || '';

        switch (status) {
            case StreamTargetStatus.IDLE:
            case StreamTargetStatus.STOPPING:
            case StreamTargetStatus.STARTING:
                return <Chip size="small" label={label} />
            case StreamTargetStatus.LIVE:
                return <Chip size="small" label={label} color="primary" />
            case StreamTargetStatus.START_FAILED:
                const chip = <Chip size="small" label={label} color="secondary" />;
                if (streamTarget && (streamTarget.errorReason || streamTarget.errorMessage)) {
                    // Get error message for tooltip
                    let errorMessage = '';
                    if (streamTarget.errorReason) {
                        errorMessage = t('app_can_go_live.' + streamTarget.errorReason, Object.assign({}, {interpolation: {escapeValue: false}}, streamTarget.errorAdditionalData));
                    } else if (streamTarget.errorMessage) {
                        errorMessage = streamTarget.errorMessage;
                    }

                    return (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {chip}
                            <Tooltip title={errorMessage} arrow>
                                <IconButton size="small" sx={{ padding: '4px', cursor: 'pointer' }}>
                                    <Info fontSize="small" sx={{ color: 'text.secondary' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }
                return chip;
            case StreamTargetStatus.STOP_FAILED:
                return <Chip size="small" label={label} color="secondary" />
            default:
                return <></>;
        }

    }

    function getIconForTarget(targetName: string) {
        const Icon = streamTargetService.getIconForTarget(targetName);
        return <Icon fontSize="small" />;
    }

    function getScheduledAccounts(streamTarget: StreamTarget) {
        return streamTarget.schedule?.accounts || [];
    }

    function renderScheduledNetworkIcons(streamTarget: StreamTarget, variant: "menu" | "row" = "row") {
        const networkTypes = Array.from(new Set(getScheduledAccounts(streamTarget).map((account) => account.type)));

        return networkTypes.map((networkType) => {
            const Icon = streamTargetService.getIconForTarget(networkType);
            return (
                <Tooltip title={t(networkType)} key={`${streamTarget.key}_${networkType}`}>
                    <Box
                        component="span"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            width: variant === "menu" ? 26 : "auto",
                            height: variant === "menu" ? 26 : "auto",
                            borderRadius: variant === "menu" ? "999px" : 0,
                            bgcolor: variant === "menu" ? "rgba(255,255,255,0.06)" : "transparent",
                            color: "text.secondary",
                        }}
                    >
                        <Icon fontSize="small" />
                    </Box>
                </Tooltip>
            );
        });
    }

    function renderScheduledAccounts(streamTarget: StreamTarget) {
        const scheduledAccounts = getScheduledAccounts(streamTarget);

        return scheduledAccounts.map((account) => {
            return (
                <Chip
                    key={`${streamTarget.key}_${account.uuid}`}
                    size="small"
                    variant="outlined"
                    icon={getIconForTarget(account.type)}
                    label={account.name}
                    sx={{
                        "& .MuiChip-label": {
                            px: 1,
                        },
                    }}
                />
            );
        });
    }

    function handleDelete(streamTarget: StreamTarget) {
        console.debug('handleDelete', streamTarget);
        removeStreamTargetFromChannelRequest(channel, streamTarget);
    }

    async function startLive(streamTarget: StreamTarget) {
        setError(undefined);

        let targets: ChannelStreamTargets = {};
        if (channel) {
            targets = channel.streamTargets;
        }

        const flag = await flagGoLive.validate({
            streamTargets: targets,
        });
        if (!flag.is_active) {
            setError(t('app_can_go_live.' + flag.reason, Object.assign({}, {interpolation: {escapeValue: false}}, flag.additional_data)));

            if (flag.reason === 'feature_flag.user.has_no_active_plan') {
                navigate('/plan');
                return;
            }

            return;
        }

        if (channel) {
            if (isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                const liveStreamUuid = ([ChannelStatus.LIVE, ChannelStatus.STARTING].includes(channel.status)
                    ? channel.liveStreamUuid
                    : `${liveEvent.code}_${uuidv4()}`);
                updateChannelStreamTargetUuidRequest(channel, liveStreamUuid);
                createAndStartLive4tvApiLiveStream({
                    profile,
                    liveStreamUuid,
                    liveEvent,
                    streamTargets: [streamTarget],
                    settings: channel.settings,
                })
            } else { // @todo: remove this else when live4tv api is released for all social networks
                createAndStartLiveStreamRequest({
                    token: profile.token,
                    liveStreamUuid: channel.liveStreamUuid,
                    liveEvent: liveEvent,
                    streamTarget: streamTarget,
                    settings: channel.settings,
                });
            }
        }
    }

    function stopLive(streamTarget: StreamTarget) {
        if (channel) {
            if (isInstanceOfLive4tvApiStreamTarget(streamTarget)) {
                stopLive4tvApiLiveStream({
                    profile,
                    liveStreamUuid: channel.liveStreamUuid,
                    streamTargets: [streamTarget],
                    settings: channel.settings,
                    liveEvent,
                })
            } else {
                stopLiveStreamRequest({
                    token: profile.token,
                    liveStreamUuid: channel.liveStreamUuid,
                    liveEvent: liveEvent,
                    streamTarget: streamTarget,
                    settings: channel.settings,
                });
            }
        }
    }

    function getChannelTargets(): Array<JSX.Element> {
        let targets: Array<any> = [];

        if (channel !== undefined) {
            Object.keys(channel.streamTargets).forEach((channelStreamTargetKey: string) => {
                let streamTarget: StreamTarget = streamTargets[channelStreamTargetKey];

                targets.push(<TableRow key={ channelStreamTargetKey }>
                    <TableCell>{getStatusChip(streamTarget.status, streamTarget)}</TableCell>
                    <TableCell>
                        <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                <Box display="flex" alignItems="center" gap={1}>
                                    {getIconForTarget(streamTarget.type)}
                                    <Typography variant="body2" fontWeight={600}>
                                        {streamTarget.title}
                                    </Typography>
                                </Box>
                                {isTargetScheduled(streamTarget) ? (
                                    <>
                                        <Chip size="small" color="secondary" label={t("Scheduled live")} />
                                        <Typography variant="caption" color="text.secondary">
                                            {t("{{count}} destinations", { count: getScheduledAccounts(streamTarget).length })}
                                        </Typography>
                                    </>
                                ) : null}
                            </Box>
                            {isTargetScheduled(streamTarget) && getScheduledAccounts(streamTarget).length > 0 ? (
                                <Box display="flex" flexWrap="wrap" alignItems="center" gap={0.75}>
                                    {renderScheduledAccounts(streamTarget)}
                                </Box>
                            ) : null}
                        </Box>
                    </TableCell>
                    <TableCell>
                        {([StreamTargetStatus.IDLE, StreamTargetStatus.START_FAILED, StreamTargetStatus.STOP_FAILED].includes(streamTarget.status)) ? (
                            <Button size="small" variant="contained" color="secondary" onClick={() => startLive(streamTarget)} startIcon={<PlayArrow />}>{t('Start')}</Button>
                        ): null}

                        {([StreamTargetStatus.STARTING, StreamTargetStatus.STOPPING].includes(streamTarget.status)) ? (
                            <CircularProgress />
                        ): null }

                        {(StreamTargetStatus.LIVE === streamTarget.status) ? (
                            <Button size="small" variant="contained" color="secondary" onClick={() => stopLive(streamTarget)} startIcon={<Stop />}>{t('Stop')}</Button>
                        ): null}
                    </TableCell>
                    <TableCell>
                        <IconButton
                            size="small"
                            color="inherit"
                            aria-label={t('Delete')}
                            onClick={ () => handleDelete(streamTarget) }
                        >
                            <Delete />
                        </IconButton>
                    </TableCell>
                </TableRow>)
            });
        }

        return targets;
    }

    return (
        <Grid container item direction="row" className={classNames(classes.root)} spacing={2}>
            <Grid item xs={12} container justifyContent="flex-start">
                <Button
                    id="add-stream-target-button"
                    color="inherit"
                    aria-controls={isAddStreamTargetMenuOpen ? 'stream-target-options-list' : undefined}
                    aria-haspopup="true"
                    aria-expanded={isAddStreamTargetMenuOpen ? 'true' : undefined}
                    onClick={(e: any) => setAddStreamTargetMenuEl(e.currentTarget)}
                >
                    <Add /> {t('Add target')}
                </Button>

                <Menu
                    id="stream-target-options-list"
                    anchorEl={addStreamTargetMenuEl}
                    open={isAddStreamTargetMenuOpen}
                    onClose={() => setAddStreamTargetMenuEl(null)}
                    MenuListProps={{
                        'aria-labelledby': 'add-stream-target-button',
                    }}
                >
                    { listStreamTargetMenuItems() }

                    <Divider />

                    { listStreamTargetIntegrationsMenuItems() }

                </Menu>
            </Grid>

            <Grid item xs={12}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getChannelTargets()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>

            <Grid item xs={12} container justifyContent="flex-end" className={classNames(classes.addTargetMenuButton)}>
                <ChannelControlStartButton
                    liveEvent={liveEvent}
                    setError={setError}
                />
            </Grid>
        </Grid>
    )
}

const mapStateToProps = function (state: any) {
    return {
        streamTargets: state.streamTargets,
        streamTargetIntegrations: state.streamTargetIntegrations,
        channels: state.channels,
        profile: state.user.profile,
    }
}

const mapDispatchToProps = function (dispatch: any) {
    return {
        fetchStreamTargetIntegrations: (apiToken: string) => dispatch(fetchStreamTargetIntegrations(apiToken)),
        addStreamTargetToChannelRequest: (channel: Channel, streamTarget: StreamTarget) => dispatch(addStreamTargetToChannelRequest(channel, streamTarget)),
        removeStreamTargetFromChannelRequest: (channel: Channel, streamTarget: StreamTarget) => dispatch(removeStreamTargetFromChannelRequest(channel, streamTarget)),
        addNewStreamTargetToChannelRequest: (channel: Channel, streamTargetIntegration: StreamTargetIntegration) => dispatch(addNewStreamTargetToChannelRequest(channel, streamTargetIntegration)),
        updateChannelStreamTargetUuidRequest: (channel: Channel, uuid: string) => dispatch(updateChannelStreamTargetUuidRequest(channel, uuid)),
        createAndStartLiveStreamRequest: (request: LiveStreamRequest) => dispatch(createAndStartLiveStreamRequest(request)),
        stopLiveStreamRequest: (request: LiveStreamRequest) => dispatch(stopLiveStreamRequest(request)),
        createAndStartLive4tvApiLiveStream: (createAndStartLiveRequest: Live4tvLiveStreamRequest) => dispatch(createAndStartLive4tvApiLiveStream(createAndStartLiveRequest)),
        stopLive4tvApiLiveStream: (stopLiveRequest: Live4tvLiveStreamRequest) => dispatch(stopLive4tvApiLiveStream(stopLiveRequest)),
        listTargets: (payload: Live4tvListStreamTargetsRequest) => dispatch(listTargets(payload)),
    }
}

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(ChannelControl));
